/** The id of the page's `<main>`: the target of the skip link. */
export const MAIN_CONTENT_ID = 'main-content';
export const SKIP_LINK_LABEL = 'Skip to main content';
const SEPARATOR = ' · ';

/** `Part · Part · Brand`, or null when no part has any text (the page keeps the title the HTML gave it). */
export function buildTitle(parts: readonly (string | null | undefined)[], brandName: string): string | null {
  const named = parts.map((p) => p?.trim()).filter((p): p is string => !!p);
  return named.length > 0 ? [...named, brandName].join(SEPARATOR) : null;
}

interface NavEntry {
  id: string;
  label?: unknown;
  items?: readonly NavEntry[];
}

/**
 * The text labels leading to the active entry, innermost first: `['Brute force', 'Rules']` for a leaf inside a group. A label
 * that is not text (an icon, a node) is skipped, and a group that repeats its leaf's label is not said twice.
 */
export function navTitleParts(sections: readonly { items: readonly NavEntry[] }[], activeId: string | undefined): string[] {
  if (!activeId) return [];
  const text = (entry: NavEntry): string[] => (typeof entry.label === 'string' && entry.label.trim() ? [entry.label.trim()] : []);
  for (const section of sections) {
    for (const entry of section.items) {
      if (entry.id === activeId) return text(entry);
      const leaf = entry.items?.find((child) => child.id === activeId);
      if (leaf) {
        const [own] = text(leaf);
        return [...text(leaf), ...text(entry).filter((group) => group !== own)];
      }
    }
  }
  return [];
}

/**
 * The parts of a page's title, innermost first: the page's own name (its last breadcrumb), the nav entry's labels, then the name of
 * the scoped console. A part that repeats the one before it is said once, so a list page titled like its nav entry stays `Websites`.
 */
export function composeTitleParts(navParts: readonly string[], leaf: string | null | undefined, scope: string | null | undefined): string[] {
  const all = [leaf, ...navParts, scope].map((p) => p?.trim()).filter((p): p is string => !!p);
  return all.filter((p, i) => p !== all[i - 1]);
}

/** What the leaf slot holds after a consumer that set `mine` goes away: its own value is cleared, another consumer's stays. */
export function clearLeaf(current: string | null, mine: string): string | null {
  return current === mine ? null : current;
}
